import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../utils/UserContext";
import { getProductReviews } from "../utils/getProductReviewsById";
import { postProductReview } from "../utils/productReview";
import { FaStar, FaChevronRight } from "react-icons/fa"; // Added chevron icon

/** Example default user icon import (adjust the path to match your project) */
// import defaultUserIcon from "../assets/default-user-icon.png";

const Star = ({ filled }) => (
  <svg
    className={`w-8 h-8 ${filled ? "text-amber-300" : "text-white stroke-amber-300"}`}
    fill="currentColor"
    viewBox="0 0 20 20"
  >
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.962a1 1 0 00.95.69h4.163c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.286 3.962c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.84-.197-1.54-1.118l1.286-3.962a1 1 0 00-.364-1.118L2.462 9.389c-.783-.57-.38-1.81.588-1.81h4.163a1 1 0 00.95-.69l1.286-3.962z" />
  </svg>
);

/**
 * Helper to ensure rating is within 5 stars
 * If a rating is above 5, it returns 5.
 */
const normalizeRating = (rating) => Math.min(rating, 5);

/**
 * CustomerReviews component
 * @param {Object} props
 * @param {string|number} props.productId - The product ID for which to fetch/post reviews.
 */
const CustomerReviews = ({ productId }) => {
  const { user, openSignInModal } = useContext(UserContext);
  const [reviews, setReviews] = useState([]);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    rating: 0,
    reviewText: "",
  });
  const [toast, setToast] = useState({ message: "", type: "" });

  // New state variables for controlling reviews display
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => {
      setToast({ message: "", type: "" });
    }, 3000);
  };

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || "",
        email: user.email || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!productId) return;
    const fetchReviews = async () => {
      try {
        const data = await getProductReviews(productId);
        setReviews(data || []);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      }
    };
    fetchReviews();
  }, [productId]);

  // Calculate the average rating using normalized values (max 5)
  const averageRating = reviews.length
    ? (
        reviews.reduce(
          (acc, cur) => acc + normalizeRating(cur.rating || 0),
          0
        ) / reviews.length
      ).toFixed(1)
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      openSignInModal();
      return;
    }
    if (!formData.rating || !formData.reviewText) {
      showToast("Please provide a rating and a review.", "error");
      return;
    }

    const payload = {
      productId: productId.toString(),
      userId: localStorage.getItem("userId") || "0",
      comments: formData.reviewText,
      rating: formData.rating,
    };

    try {
      await postProductReview(payload);
      showToast("Review submitted successfully!", "success");
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        rating: 0,
        reviewText: "",
      });
      const data = await getProductReviews(productId);
      setReviews(data || []);
    } catch (err) {
      console.error("Error submitting review:", err);
      showToast("Failed to submit review. Please try again later.", "error");
    }
  };

  // Compute star distribution using normalized ratings
  const distribution = [5, 4, 3, 2, 1].map((star) => {
    const count = reviews.filter(
      (r) => normalizeRating(r.rating) === star
    ).length;
    const percentage = reviews.length
      ? Math.round((count / reviews.length) * 100)
      : 0;
    return { star, count, percentage };
  });

  // Prepare sorted reviews (newest first)
  const sortedReviews = reviews.slice().sort(
    (a, b) => new Date(b.reviewDate) - new Date(a.reviewDate)
  );
  const displayedReviews = sortedReviews.slice(0, visibleCount);

  // Handler for "See more reviews" button
  const handleSeeMore = () => {
    setShowAllReviews(true);
    // If more than 10 reviews exist, initially show 10; otherwise show all
    const initialCount = reviews.length > 10 ? 10 : reviews.length;
    setVisibleCount(initialCount);
  };

  // Infinite scroll: when user scrolls near bottom, load more reviews
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      if (visibleCount < sortedReviews.length) {
        setVisibleCount((prev) => Math.min(prev + 5, sortedReviews.length));
      }
    }
  };

  return (
    <div className="relative">
      {toast.message && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded shadow ${
            toast.type === "success"
              ? "bg-green-50 text-green-600"
              : "bg-red-50 text-red-600"
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="md:mb-8 mb-5">
        <h2 className="text-center text-black text-2xl md:text-4xl font-normal font-['Poppins'] md:leading-[48px]">
          Customer Reviews
        </h2>
      </div>

      <div className="flex flex-col md:flex-row gap-8 mb-10">
        <div className="flex flex-col justify-center items-center w-full md:w-1/3 h-52 bg-blue-50 rounded-md border border-blue-300">
          <div className="text-blue-400 text-3xl font-semibold font-['Poppins']">
            {averageRating}
          </div>
          <div className="flex space-x-3 mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} filled={i < Math.round(averageRating)} />
            ))}
          </div>
          <p className="text-center text-black text-sm font-medium">
            {reviews.length} Ratings
          </p>
        </div>
        <div className="w-full md:w-2/3 space-y-7">
          {distribution.map((item) => (
            <div key={item.star} className="flex items-center">
              <span className="w-12 text-black text-sm font-medium">
                {item.star} Star
              </span>
              <div className="flex-1 h-2 bg-sky-100 rounded-md mx-3">
                <div
                  className={`h-2 rounded ${
                    item.star >= 3 ? "bg-green-500" : "bg-red-500"
                  }`}
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
              <span className="text-right text-black text-sm font-medium">
                {item.count}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 md:grid-cols-2 gap-8 lg:gap-10">
        <div className="lg:col-span-5">
          <div>
            <h3 className="text-2xl font-medium mb-4">Add a Review</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium text-sm">
                  Your Rating
                </label>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, rating: index + 1 }))
                      }
                    >
                      <Star filled={index < formData.rating} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block mb-1 font-medium text-sm">Name</label>
                <input
                  type="text"
                  className="w-full p-2 text-sm bg-blue-50 rounded-md border border-blue-300"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  readOnly={!!user}
                />
              </div>
              <div>
                <label className="block mb-1 font-medium text-sm">Email</label>
                <input
                  type="email"
                  className="w-full p-2 text-sm bg-blue-50 rounded-md border border-blue-300"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  readOnly={!!user}
                />
              </div>
              <div>
                <label className="block mb-1 font-medium text-sm">
                  Write your Review
                </label>
                <textarea
                  className="w-full p-2 text-sm bg-blue-50 rounded-md border border-blue-300"
                  rows="4"
                  placeholder="Write your review here..."
                  value={formData.reviewText}
                  onChange={(e) =>
                    setFormData({ ...formData, reviewText: e.target.value })
                  }
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-gradient-to-br from-blue-400 to-blue-500 rounded-3xl text-white shadow-[inset_0px_0.5px_0px_0px_rgba(255,255,255,0.32)] inline-flex justify-center items-center"
              >
                Submit Review
              </button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-7">
          {reviews.length === 0 ? (
            <div className="p-4 bg-blue-50 rounded text-center">
              No reviews yet. Be the first to comment!
            </div>
          ) : (
            <div>
              <h3 className="text-black text-3xl font-medium font-['Poppins'] leading-10 mb-4">
                Reviews
              </h3>
              {showAllReviews ? (
                <div
                  className="max-h-[800px] review-scroll overflow-y-auto space-y-6 pr-3"
                  onScroll={handleScroll}
                >
                  {displayedReviews.map((rev) => {
                    const reviewDate = rev.reviewDate
                      ? new Date(rev.reviewDate).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "21 Jan, 2025"; // fallback date if none provided
                    return (
                      <div
                        key={rev.id}
                        className="bg-white rounded-lg border border-stone-300 shadow p-5"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <img
                              src="https://content.qeeb.in/coltec/default-avatar.png"
                              alt={rev.userName}
                              className="md:h-9 md:w-9 w-7 h-7 rounded-full mr-3"
                            />
                            <div className="leading-4">
                              <p className="font-normal text-sm md:text-[15px]">
                                {rev.userName || rev.userId || "Anonymous"}
                              </p>
                              <span className="text-black text-xs">
                                {reviewDate}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div
                              className={`flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ${
                                normalizeRating(rev.rating) >= 3
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              } text-white`}
                            >
                              <p className="text-sm mr-1">
                                {normalizeRating(rev.rating)}
                              </p>
                              <FaStar className="text-sm mt-[-1px]" />
                            </div>
                          </div>
                        </div>
                        <p className="text-sm md:text-[15px] mt-3 max-w-xl">
                          {rev.comments}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-6">
                  {displayedReviews.map((rev) => {
                    const reviewDate = rev.reviewDate
                      ? new Date(rev.reviewDate).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "21 Jan, 2025"; // fallback date if none provided
                    return (
                      <div
                        key={rev.id}
                        className="bg-white rounded-lg border border-stone-300 shadow p-5"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <img
                              src="https://content.qeeb.in/coltec/default-avatar.png"
                              alt={rev.userName}
                              className="md:h-9 md:w-9 w-7 h-7 rounded-full mr-3"
                            />
                            <div className="leading-4">
                              <p className="font-normal text-sm md:text-[15px]">
                                {rev.userName || rev.userId || "Anonymous"}
                              </p>
                              <span className="text-black text-xs">
                                {reviewDate}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div
                              className={`flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium ${
                                normalizeRating(rev.rating) >= 3
                                  ? "bg-green-500"
                                  : "bg-red-500"
                              } text-white`}
                            >
                              <p className="text-sm mr-1">
                                {normalizeRating(rev.rating)}
                              </p>
                              <FaStar className="text-sm mt-[-1px]" />
                            </div>
                          </div>
                        </div>
                        <p className="text-sm md:text-[15px] mt-3 max-w-xl">
                          {rev.comments}
                        </p>
                      </div>
                    );
                  })}
                  {reviews.length > 3 && (
                    <button
                      onClick={handleSeeMore}
                      className="flex text-sm items-center text-blue-500"
                    >
                      <span>See more reviews</span>
                      <FaChevronRight className="ml-1 text-sm" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerReviews;
